import Cookies from "universal-cookie";
import axios from "axios";
import { sanitizeUserInput, loginRateLimiter, detectSuspiciousActivity } from "../../utils/security";

export function getToken() {
  const cookies = new Cookies();
  const token = cookies.get("token");
  
  // Note: Laravel Sanctum tokens are not JWTs, so we skip format validation
  // Basic XSS protection only
  if (token && (token.includes('<script') || token.includes('javascript:'))) {
    console.warn('Suspicious token content detected, removing token');
    removeToken();
    return null;
  }
  
  return token;
}

function getCurrentTeamUuid() {
  const cookies = new Cookies();
  const currentTeam = cookies.get("currentTeam");
  
  if (currentTeam) {
    try {
      const team = JSON.parse(currentTeam);
      return team.uuid;
    } catch (e) {
      console.warn('Failed to parse current team from cookies');
    }
  }
  
  // Fallback to legacy team_uuid cookie
  return cookies.get("team_uuid");
}

function injectTeamContext(url, options = {}) {
  const teamUuid = getCurrentTeamUuid();
  
  if (!teamUuid) {
    return { url, options };
  }
  
  // Check if URL contains {team_uuid} placeholder
  if (url.includes('{team_uuid}')) {
    url = url.replace('{team_uuid}', teamUuid);
  } else if (isTeamScopedEndpoint(url)) {
    // Add as query parameter for team-scoped endpoints
    const separator = url.includes('?') ? '&' : '?';
    url += `${separator}team_uuid=${teamUuid}`;
  }
  
  return { url, options };
}

function isTeamScopedEndpoint(url) {
  const teamScopedPaths = [
    '/tier/',
    '/team/',
    '/statistics/',
    '/get_credits/',
    '/projects/',
    '/all_analysis/',
    '/credit-usage-logs',
    '/purchase-entries',
    '/research-pipeline'
  ];
  
  return teamScopedPaths.some(path => url.includes(path));
}

export function removeToken() {
  const cookies = new Cookies();
  cookies.remove("user_id")
  cookies.remove("credits")
  cookies.remove("token");
  cookies.remove("team_uuid");
  cookies.remove("teams");
  cookies.remove("currentTeam");
  cookies.remove('bannerDismissed', { path: '/' })
  return 200
}

axios.interceptors.response.use(
  function (response) {
    return response;
  },
  function (error) {
    // Do something with response error
    if (error.response && error.response.status === 401) {
      const cookies = new Cookies();
      cookies.remove("user_id");
      cookies.remove("token");
      cookies.remove("teams");
      cookies.remove("currentTeam");
      cookies.remove("team_uuid");
      if (!error.config.url.includes("/api/login") &&
        !error.config.url.includes("/api/end_user_login") &&
        !error.config.url.includes("request_end_user_password_change")) {
        window.location.href = '/';
      }
    }
    
    // Handle team-related errors
    if (error.response) {
      const { status, data } = error.response;
      
      if (status === 403 && data?.error && data.error.toLowerCase().includes('access denied')) {
        console.warn('Access denied - redirecting to dashboard:', data.error);
        // Redirect user to dashboard when accessing any unauthorized resources
        window.location.href = '/dashboard';
        return Promise.reject(error);
      }
      
      if (status === 400 && data?.error === 'Team context required') {
        console.warn('Team context required - missing team_uuid parameter');
        // Let the component handle this error
      }
      
      if (status === 500 && data?.message === 'Team validation failed') {
        console.error('Team validation failed on server');
        // Let the component handle this error
      }
    }
    
    return Promise.reject(error);
  }
);


export function login(email, password) {
  // Sanitize inputs
  const sanitizedEmail = sanitizeUserInput(email);
  const sanitizedPassword = sanitizeUserInput(password);
  
  // Rate limiting check
  if (!loginRateLimiter.isAllowed(sanitizedEmail)) {
    return Promise.reject(new Error('Too many login attempts. Please try again later.'));
  }
  
  // Detect suspicious activity
  if (detectSuspiciousActivity(navigator.userAgent)) {
    console.warn('Suspicious activity detected during login attempt');
  }
  
  var url = process.env.REACT_APP_API_BASE_URL + `/login`;
  return axios.post(url, {
    email: sanitizedEmail,
    password: sanitizedPassword,
  });
}

export function logout() {
  removeToken()
  var url = process.env.REACT_APP_API_BASE_URL + `/logout`;
  return axios.get(url);
}

export function register(data) {
  var url = process.env.REACT_APP_API_BASE_URL + `/register`;
  return axios.post(url, data);
}

export function confirmEmail(confirmation_token) {
  var url = process.env.REACT_APP_API_BASE_URL + `/confirm_email`;
  return axios.post(url, {
    confirmation_token: confirmation_token,
  });
}


export function getAvailablePlans() {
  var url = process.env.REACT_APP_API_BASE_URL + "/plans";

  return axios.get(url, { headers: { authorization: `Bearer ${getToken()}` } });
}

export function createNewSubscription(data) {
  var url = process.env.REACT_APP_API_BASE_URL + "/create_new_subscription";
  return axios.post(
    url,
    data,
    { headers: { authorization: `Bearer ${getToken()}` } }
  );
}


export function getSubscriptions() {
  var url = process.env.REACT_APP_API_BASE_URL + "/subscriptions";
  return axios.get(url, { headers: { authorization: `Bearer ${getToken()}` } });
}

export function cancelSubscriptionRenewal(data) {
  var url = process.env.REACT_APP_API_BASE_URL + "/cancel_subscription_renewal";
  return axios.post(
    url,
    data,
    { headers: { authorization: `Bearer ${getToken()}` } }
  );
}

export function reactivateSubscriptionRenewal(subscription_id) {
  var url =
    process.env.REACT_APP_API_BASE_URL + "/reactivate_subscription_renewal";
  return axios.post(
    url,
    { subscription_id: subscription_id },
    { headers: { authorization: `Bearer ${getToken()}` } }
  );
}

export function requestPasswordChange(email) {
  var url =
    process.env.REACT_APP_API_BASE_URL + "/request_password_change";
  return axios.post(
    url,
    {
      email: email
    },
    { headers: { authorization: `Bearer ${getToken()}` } }
  );
}

export function changePassword(confirmation_token, password, password_confirmation) {
  var url =
    process.env.REACT_APP_API_BASE_URL + "/change_password";
  return axios.post(
    url,
    {
      confirmation_token: confirmation_token, password: password,
      password_confirmation: password_confirmation
    },
    { headers: { authorization: `Bearer ${getToken()}` } }
  );
}

export function updatePassword(old_password, password, password_confirmation) {
  var url =
    process.env.REACT_APP_API_BASE_URL + "/setup_new_password";
  return axios.post(
    url,
    {
      old_password: old_password,
      password: password,
      password_confirmation: password_confirmation
    },
    { headers: { authorization: `Bearer ${getToken()}` } }
  );
}

export function updateEmailSettings(email_updates, email_promotions) {
  var url =
    process.env.REACT_APP_API_BASE_URL + "/update_email_settings";
  return axios.post(
    url,
    {
      email_updates: email_updates,
      email_promotions: email_promotions
    },
    { headers: { authorization: `Bearer ${getToken()}` } }
  );
}

export function getEmailSettings() {
  var url =
    process.env.REACT_APP_API_BASE_URL + "/email_settings";
  return axios.get(url, { headers: { authorization: `Bearer ${getToken()}` } }
  );
}

export function deleteUser() {
  var url =
    process.env.REACT_APP_API_BASE_URL + "/delete";
  return axios.get(url, { headers: { authorization: `Bearer ${getToken()}` } }
  );
}

export function registerByLink(data) {
  var url = process.env.REACT_APP_API_BASE_URL + `/register_by_link`;
  return axios.post(url, data);
}

export function getInviteLink(uuid) {
  var url = process.env.REACT_APP_API_BASE_URL + `/get_invite_link/` + uuid;
  return axios.get(url, { headers: { authorization: `Bearer ${getToken()}` } })
}

export function getReferalLink() {
  var url = process.env.REACT_APP_API_BASE_URL + `/get_referral_link`;
  return axios.get(url, { headers: { authorization: `Bearer ${getToken()}` } })
}

export function getRefees() {
  var url = process.env.REACT_APP_API_BASE_URL + `/get_referees`;
  return axios.get(url, { headers: { authorization: `Bearer ${getToken()}` } })
}

export function requestGdprExport(format = 'csv') {
  var url = process.env.REACT_APP_API_BASE_URL + `/gdpr/export?format=${format}`;
  return axios.get(url, { 
    headers: { authorization: `Bearer ${getToken()}` },
    responseType: 'blob'
  });
}

export function getUserTeams() {
  var url = process.env.REACT_APP_API_BASE_URL + `/user/teams`;
  return axios.get(url, { 
    headers: { authorization: `Bearer ${getToken()}` }
  });
}

export function switchTeam(teamUuid) {
  var url = process.env.REACT_APP_API_BASE_URL + `/user/switch-team/${teamUuid}`;
  return axios.post(url, {}, { 
    headers: { authorization: `Bearer ${getToken()}` }
  });
}

export function createTeam(teamData) {
  var url = process.env.REACT_APP_API_BASE_URL + `/teams`;
  return axios.post(url, teamData, { 
    headers: { authorization: `Bearer ${getToken()}` }
  });
}

export function updateTeam(teamUuid, teamData) {
  var url = process.env.REACT_APP_API_BASE_URL + `/teams/${teamUuid}`;
  return axios.put(url, teamData, { 
    headers: { authorization: `Bearer ${getToken()}` }
  });
}
